/**
 * Filter chips — shared single-select behaviour.
 *
 * Product-type chips (`snippets/collection-products.liquid`) and the
 * quick-filter cards (`snippets/filter-cards.liquid`) are both "pick one"
 * UIs: selecting one clears the rest, and pressing the active one clears it
 * and restores all products.
 *
 * The chip handler is installed ONCE on `document` in the capture phase, so
 * it is independent of which section script (collection / collection-poster /
 * designer / brand / publisher) happens to own the markup, runs before any
 * per-input `change` listener composes filter state, and keeps working when
 * a section, grid or filter region is re-rendered or swapped by fetch.
 */

// Type chips rendered by snippets/collection-products.liquid.
const TYPE_CHIP_SELECTOR = '.type-filter input[name="product_type"]';

// Params that must never carry more than one value.
const SINGLE_VALUE_PARAMS = ['product_type', 'filter.p.product_type'];

let chipsInstalled = false;

/**
 * The element that scopes a chip group. Prefer the explicit `.type-filters`
 * row, then the owning section, so two grids on one page stay independent.
 */
const groupRoot = (input) =>
  input.closest('.type-filters') ||
  input.closest('[data-section-id]') ||
  input.closest('.shopify-section') ||
  document;

/**
 * Uncheck every chip in `input`'s group except `input` itself. Safe to call
 * with an unchecked input (pressing the active chip clears it — nothing else
 * needs to change, the empty selection means "all products").
 */
export const enforceSingleChip = (input) => {
  if (!input || !input.checked) return;
  groupRoot(input)
    .querySelectorAll(TYPE_CHIP_SELECTOR)
    .forEach((chip) => {
      if (chip !== input) chip.checked = false;
    });
};

const onChipChangeCapture = (e) => {
  const input = e.target;
  if (!input || typeof input.matches !== 'function') return;
  if (!input.matches(TYPE_CHIP_SELECTOR)) return;
  enforceSingleChip(input);
};

/**
 * Install the global chip handler. Idempotent — call it from anywhere that
 * may own chips (main.js boot, a section script constructor).
 */
export const initFilterChips = () => {
  if (chipsInstalled) return;
  chipsInstalled = true;
  document.addEventListener('change', onChipChangeCapture, true);
};

/**
 * Collapse repeated values of the single-select params down to the first one,
 * so a URL can never end up filtering by two product types at once. Other
 * params (colour, size, …) stay multi-value — those filters are combinable.
 *
 * @param {string} rawUrl - Absolute or relative URL.
 * @returns {string} The normalised URL (unchanged input on parse failure).
 */
export const collapseSingleValueParams = (rawUrl) => {
  if (!rawUrl) return rawUrl;

  let url;
  try {
    url = new URL(rawUrl, window.location.origin);
  } catch (error) {
    return rawUrl;
  }

  SINGLE_VALUE_PARAMS.forEach((key) => {
    const values = url.searchParams.getAll(key);
    if (values.length > 1) {
      url.searchParams.delete(key);
      url.searchParams.set(key, values[0]);
    }
  });

  return url.toString();
};

/**
 * Build the URL that clears an already-applied quick-filter card: the current
 * location minus every `filter.*` param the card applies. Non-filter params
 * (sort_by, view, …) are preserved.
 *
 * @param {string} cardHref - The card's own filter URL.
 * @returns {string} URL with the card's filters removed.
 */
export const urlWithoutCardFilters = (cardHref) => {
  const current = new URL(window.location.href);

  let card;
  try {
    card = new URL(cardHref, window.location.origin);
  } catch (error) {
    return current.toString();
  }

  card.searchParams.forEach((value, key) => {
    if (key.startsWith('filter.')) current.searchParams.delete(key);
  });

  return current.toString();
};
